import cx from 'classnames';
import { t } from 'i18next';
import qs from 'qs';
import Menu, { Divider, MenuItem, SubMenu } from 'rc-menu';
import { useLocation } from 'react-router';
import { ProfileMasthead } from '../../../../../../shared/frontend/components/character/profileMasthead';
import { useGuildPagesVisibility } from '../../../../../../shared/frontend/components/guild/hooks/useGuildPagesVisibility';
import { Link, TrackedLink } from '../../../../../../shared/frontend/components/link';
import { menuBuiltinPlacements } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { TSiteMetaProps, withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { handleRCMenuOnClick } from '../../../../../../shared/frontend/libs/history';
import { formatRealmDisplayName } from '../../../../../../shared/common/utils';
import { buildRealmSlug } from '../../../../../../shared/frontend/libs/utils';
import { QualifiedTagBadge } from '../../../../../../shared/frontend/scripts/recruitment/components/QualifiedTagBadge';
import { EXPANSIONS_INFO, RAID_METADATA_BY_SLUG, SEASONS_SORTED } from '../../../../shared/enums';
import { buildGuildArmoryUrl } from '../../../../shared/utils';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import WowIcon from '../../../../../../shared/frontend/scripts/wow/components/WowIcon';
import { scaledAssetUrl } from '../../../../../../shared/frontend/services/assetResolverService';
import { isHexColor, legibleOnDark } from '../../../../../../shared/frontend/libs/colors';

const ACHIEVEMENT_TIERS = [
  // An unreleased tier is gated on its raid's unlock date via `raidSlug`: it stays out of the
  // dropdown until that raid's `starts` time (for the viewed guild's region) has passed. The
  // check runs at render time, so the tier shows up on the next (uncached) page render after the
  // unlock without a code change — though an already-open tab or cached HTML won't reflect it
  // until it re-renders. The raid's "Glory of the ..." meta achievement IDs still have to be
  // tagged in the backend (ALL_META_ACHIEVEMENTS / getTaggedFields), which normally lands during
  // raid setup; until then the guild achievements page shows a graceful empty state. Older tiers
  // omit `raidSlug` and are always shown.
  { tier: 'tier36', label: 'achievements:glory.tier36', raidSlug: 'the-venomous-abyss' },
  { tier: 'tier35', label: 'achievements:glory.tier35' },
  { tier: 'tier34', label: 'achievements:glory.tier34' },
  { tier: 'tier33', label: 'achievements:glory.tier33' },
  { tier: 'tier32', label: 'achievements:glory.tier32' },
  { tier: 'tier31', label: 'achievements:glory.tier31' },
  { tier: 'tier30', label: 'achievements:glory.tier30' },
  { tier: 'tier29', label: 'achievements:glory.tier29' },
  { tier: 'tier28', label: 'achievements:glory.tier28' },
  { tier: 'tier27', label: 'achievements:glory.tier27' },
  { tier: 'tier26', label: 'achievements:glory.tier26' },
  { tier: 'tier25', label: 'achievements:glory.tier25' },
  { tier: 'tier24', label: 'achievements:glory.tier24' },
  { tier: 'tier23', label: 'achievements:glory.tier23' },
  { tier: 'tier22', label: 'achievements:glory.tier22' },
  { tier: 'tier21', label: 'achievements:glory.tier21' },
  { tier: 'tier20', label: 'achievements:glory.tier20' },
  { tier: 'tier19', label: 'achievements:glory.tier19' }
];

// A tier is shown once its gating raid has unlocked for the given region. Tiers without a
// `raidSlug` (every already-released tier) are always shown.
function isAchievementTierUnlocked(tinfo: { raidSlug?: string }, regionSlug: string): boolean {
  if (!tinfo.raidSlug) {
    return true;
  }
  const starts = RAID_METADATA_BY_SLUG[tinfo.raidSlug]?.starts as Record<string, any> | undefined;
  const startsAt = starts?.[regionSlug] ?? starts?.us;
  return !!startsAt && startsAt.valueOf() <= Date.now();
}

export const GuildProfileUnit = withSiteMeta((props: any) => {
  // When the guild has a brand color, color the name with a luminance-floored
  // variant (readable over any banner) and swap the faction color class — which
  // sets `color` with !important and so can't be overridden inline — for a
  // dedicated class carrying a dark halo shadow. Colorless guilds are untouched.
  const hasBrandColor = isHexColor(props.guild.color);
  const nameClassName = hasBrandColor
    ? 'rio-guild-brand-name'
    : `rio-text-shadow--heavy rio-faction-color--${props.guild.faction}`;
  const nameStyle = hasBrandColor ? { color: legibleOnDark(props.guild.color) } : undefined;

  // Only guilds with real art get a logo: `isDefaultLogo` means buildGuildSummary fell all the
  // way through to the faction crest, which every guild would otherwise show here.
  const hasLogo = Boolean(props.guild.logo) && !props.guild.isDefaultLogo;

  return (
    <div className="slds-p-horizontal--small slds-m-bottom--medium rio-align-center--max-small">
      <div className="slds-grid slds-grid--vertical-align-center">
        {hasLogo && (
          <div className="slds-col slds-grow-none slds-p-right--x-small">
            <img
              className="rio-guild-masthead-logo"
              src={scaledAssetUrl(props.guild.logo, { height: 128 })}
              alt={`${props.guild.name} logo`}
            />
          </div>
        )}
        <div className="slds-col slds-grow-none slds-p-right--x-small">
          <QualifiedTagBadge guildId={props.guild.id} />
        </div>
        <div className="slds-col">
          <h3 className={'d-inline-block slds-text-heading--large'}>
            {props.siteConfig.isEmbedded ? (
              <Link className={nameClassName} style={nameStyle} to={props.guild.path}>
                {props.guild.displayName}
              </Link>
            ) : (
              <span className={nameClassName} style={nameStyle}>
                {props.guild.displayName}
              </span>
            )}
          </h3>

          {!props.siteConfig.isEmbedded && (
            <TrackedLink
              href={buildGuildArmoryUrl(props.guild.region.slug, props.guild.realm.slug, props.guild.name)}
              title={t('guilds:link-armory')}
              target="_blank"
              className="rio-realm-link slds-text-body--small slds-m-left--x-small"
              as="guild:armory-link-icon"
            >
              <i className="fa fa-external-link" />
            </TrackedLink>
          )}

          <div className="text-white slds-text-body--regular">
            <Link
              className="rio-realm-link rio-text-shadow--normal"
              to={resolveRoutePath(namedRoutes.RegionHome, {
                region: props.guild.region.slug
              })}
            >
              ({t(`regions:${props.guild.region.slug}.short-name`)})
            </Link>
            &nbsp;
            <Link
              to={resolveRoutePath(namedRoutes.RealmHome, {
                region: props.guild.region.slug,
                realm: buildRealmSlug(props.guild.realm)
              })}
              className="rio-realm-link rio-text-shadow--normal"
            >
              {formatRealmDisplayName(props.guild.realm)}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
});

export function GuildMasthead(props: any) {
  const location = useLocation();
  const qParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const banner = props.banner_asset_name || `${props.guild.faction}banner1`;

  return (
    <ProfileMasthead
      className={cx({ 'rio-masthead--brandable': isHexColor(props.guild?.color) }, props.className)}
      bannerAssetName={banner}
      target={<GuildProfileUnit {...props} />}
      highlights={props.highlights}
      watermark={props.watermark}
      faction={props.guild ? props.guild.faction : undefined}
      forceBannerUrl={qParams.debug_force_banner}
      isAnimated={props.isAnimated}
    >
      {props.children}
    </ProfileMasthead>
  );
}

type TGuildSubNavProps = TSiteMetaProps & {
  guild: any;
  match: any;
  streamers: any;
  className?: string;
  recruitmentProfiles: any[];
};

export const GuildSubNav = withSiteMeta((props: TGuildSubNavProps) => {
  const rosterPath = resolveRoutePath(namedRoutes.GuildRoster, {
    region: props.guild.region.slug,
    realm: props.guild.realm.slug,
    guild: props.guild.name
  });

  const { stats, profile, roster, mythicPlus, raidAchievements, recruitment } = useGuildPagesVisibility();

  const hasRecruitment = props.currentFeatures.recruitment_view && props.recruitmentProfiles?.length > 0;

  return (
    <div className={props.className}>
      <Menu onClick={handleRCMenuOnClick} selectable={false} mode="horizontal" disabledOverflow={true} className="rio-title-menu" builtinPlacements={menuBuiltinPlacements}>
        <MenuItem
          className={cx('rio-guild-tab', {
            'rio-guild-tab--active': profile
          })}
          key={resolveRoutePath(namedRoutes.GuildDetails, {
            region: props.guild.region.slug,
            realm: props.guild.realm.slug,
            guild: props.guild.name
          })}
        >
          {t('guilds:tabs.profile')}
        </MenuItem>

        <MenuItem
          className={cx('rio-guild-tab', {
            'rio-guild-tab--active': stats
          })}
          key={resolveRoutePath(namedRoutes.GuildStats, {
            region: props.guild.region.slug,
            realm: props.guild.realm.slug,
            guild: props.guild.name
          })}
        >
          <span>
            {t('guilds:tabs.stats')}
            <span className="slds-m-left--x-small new-feature-indicator">{t('nav:suffixes.beta')}</span>
          </span>
        </MenuItem>

        <SubMenu
          key={`${rosterPath}#mode=all`}
          onTitleClick={(params: any) => handleRCMenuOnClick(params, 'mode=all')}
          className={cx('rio-guild-tab', {
            'rio-guild-tab--active': roster
          })}
          title={
            <span>
              {t('guilds:roster.title')} <i className="slds-m-left--x-small fa fa-caret-down text-raiderio" />
            </span>
          }
        >
          <MenuItem key={`${rosterPath}#mode=mythic_plus`}>{t('guilds:roster.all-mplus')}</MenuItem>
          <MenuItem key={`${rosterPath}#mode=raiders_or_mythic_plus`}>
            {t('guilds:roster.all-raiders-and-runners')}
          </MenuItem>
          <SubMenu
            key={`${rosterPath}#mode=raiders`}
            onTitleClick={(params: any) => handleRCMenuOnClick(params, 'mode=raiders')}
            title={t('guilds:roster.all-raiders')}
          >
            <MenuItem key={`${rosterPath}#mode=normal_raiders`}>
              {t('guilds:roster.difficulty-raiders', {
                difficulty: t('common:difficulty.normal.name')
              })}
            </MenuItem>
            <MenuItem key={`${rosterPath}#mode=heroic_raiders`}>
              {t('guilds:roster.difficulty-raiders', {
                difficulty: t('common:difficulty.heroic.name')
              })}
            </MenuItem>
            <MenuItem key={`${rosterPath}#mode=mythic_raiders`}>
              {t('guilds:roster.difficulty-raiders', {
                difficulty: t('common:difficulty.mythic.name')
              })}
            </MenuItem>
          </SubMenu>
          {props.streamers.count > 0 && (
            <MenuItem key={`${rosterPath}#mode=streamers`}>
              {t('guilds:roster.all-active')}{' '}
              <i className="slds-avatar slds-avatar--x-small rio-profile-links rio-profile-links--small twitch slds-m-left--x-small" />
            </MenuItem>
          )}
          <Divider />
          <MenuItem key={`${rosterPath}#mode=all`}>{t('guilds:roster.all-characters')}</MenuItem>
        </SubMenu>

        <SubMenu
          title={
            <span>
              {t('guilds:tabs.mythic-plus')} <i className="slds-m-left--x-small fa fa-caret-down text-raiderio" />
            </span>
          }
          key={resolveRoutePath(namedRoutes.GuildCharacterKeystoneLeaderboard, {
            region: props.guild.region.slug,
            realm: props.guild.realm.slug,
            guild: props.guild.name,
            season: props.siteConfig.currentSeasonSlug
          })}
          onTitleClick={handleRCMenuOnClick}
          className={cx('rio-guild-tab', {
            'rio-guild-tab--active': mythicPlus
          })}
        >
          {/* current expansion shows expanded */}
          {EXPANSIONS_INFO.slice(0, 1).map((expansion) =>
            SEASONS_SORTED.filter(
              (seasonInfo) => seasonInfo.expansionId === expansion.expansionId && !seasonInfo.hidden
            ).map((seasonInfo) => (
              <MenuItem
                key={resolveRoutePath(namedRoutes.GuildCharacterKeystoneLeaderboard, {
                  region: props.guild.region.slug,
                  realm: props.guild.realm.slug,
                  guild: props.guild.name,
                  season: seasonInfo.slug
                })}
              >
                {seasonInfo.icon && (
                  <WowIcon lazy icon={seasonInfo.icon} size="xx-small" className="slds-m-right--x-small" />
                )}
                <span>
                    {t(`seasons:${seasonInfo.slug}.title`)}
                  </span>
              </MenuItem>
            ))
          )}
          {/* previous expansions collapse into submenus */}
          {EXPANSIONS_INFO.slice(1).map((expansion) => (
            <SubMenu title={<span>
              <WowIcon lazy icon={expansion.icon || 'inv_misc_token_boost_generic'} size="xx-small" className="slds-m-right--x-small" />
              {expansion.name}
            </span>}>
              {SEASONS_SORTED.filter(
                (seasonInfo) => seasonInfo.expansionId === expansion.expansionId && !seasonInfo.hidden
              ).map((seasonInfo) => (
                <MenuItem
                  key={resolveRoutePath(namedRoutes.GuildCharacterKeystoneLeaderboard, {
                    region: props.guild.region.slug,
                    realm: props.guild.realm.slug,
                    guild: props.guild.name,
                    season: seasonInfo.slug
                  })}
                >
                  {seasonInfo.icon && (
                    <WowIcon lazy icon={seasonInfo.icon} size="xx-small" className="slds-m-right--x-small" />
                  )}
                  <span>
                    {t(`seasons:${seasonInfo.slug}.title`)}
                  </span>
                </MenuItem>
              ))}
            </SubMenu>
          ))}
        </SubMenu>

        <SubMenu
          className={cx('rio-guild-tab', {
            'rio-guild-tab--active': raidAchievements
          })}
          title={
            <span>
              {t('guilds:tabs.raid-achievements')} <i className="slds-m-left--x-small fa fa-caret-down text-raiderio" />
            </span>
          }
        >
          {ACHIEVEMENT_TIERS.filter((tinfo: any) => isAchievementTierUnlocked(tinfo, props.guild.region.slug)).map(
            (tinfo: any) => (
            <MenuItem
              key={resolveRoutePath(namedRoutes.GuildCharacterAchievementLeaderboard, {
                region: props.guild.region.slug,
                realm: props.guild.realm.slug,
                guild: props.guild.name,
                tier: tinfo.tier
              })}
            >
              {t(tinfo.label)}
            </MenuItem>
          ))}
        </SubMenu>

        <MenuItem
          className="rio-guild-tab"
          key={
            resolveRoutePath(namedRoutes.Widget) +
            '?' +
            qs.stringify({
              guild: props.guild.name,
              realm: props.guild.realm.slug,
              region: props.guild.region.slug
            })
          }
        >
          {t('guilds:tabs.widgets')}
        </MenuItem>

        {hasRecruitment && (
          <MenuItem
            className={cx('rio-guild-tab', {
              'rio-guild-tab--active': recruitment
            })}
            key={resolveRoutePath(namedRoutes.GuildRecruitmentPage, {
              region: props.guild.region.slug,
              realm: props.guild.realm.slug,
              guild: props.guild.name
            })}
          >
            {t('guilds:tabs.recruitment')}
          </MenuItem>
        )}

        {props.streamers.count > 0 && (
          <MenuItem className={cx('rio-guild-tab')} key={`${rosterPath}#mode=streamers`}>
            <i className="slds-avatar slds-avatar--x-small rio-profile-links rio-profile-links--small twitch slds-m-right--x-small" />
            {props.streamers.count > 1 ? `${props.streamers.count}` : ''} {t('guilds:tabs.streaming-now')}
          </MenuItem>
        )}

        {/* <SubMenu
          onTitleClick={() => history.push(mythicPlusCharactersPath)}
          className={cx('rio-guild-tab', { 'rio-guild-tab--active': stripPathLocale(props.match.path) === GuildCharacterKeystoneLeaderboardPage})}
          title={<span>Guild Mythic Plus <i className="slds-m-left--x-small fa fa-caret-down text-raiderio"></i></span>}>
          <MenuItem key={resolveRoutePath(namedRoutes.GuildCharacterKeystoneLeaderboard, {
            region: props.guild.region.slug,
            realm: props.guild.realm.slug,
            guild: props.guild.name
          })}>
            <span className="rio-hidden--small slds-avatar slds-avatar--x-small slds-m-right--x-small rio-border--light rio-shadow--small">
              <img src={asset('/images/wow/icons/medium/achievement_guildperk_everyonesahero.jpg')} />
            </span>
            &nbsp;Characters Leaderboard
          </MenuItem>
        </SubMenu> */}
      </Menu>
    </div>
  );
});
